import { FieldPath, FieldValues } from 'react-hook-form'

import { ActionStatus } from '../../types/Input'

export type CreateDefectPayload = {
  affectToUsage: string
  comment: string
  criticality: number
  defectGroup: string
  installationGroup: string
  installationItem: string
  installationSerialNumber: string
  issuedTime: string
  reportedDefectCode: string
  restrictionCode: string
  vehiclePosition: string
}

export type CreateDefectFormData = {
  issuedTime: Date
  comment: string
  aiComment: string
  aiAssisted: boolean
  digitalAssetUuids: string[]
  defectGroup: string
  installationGroup: string
  defectCodePrefix: string
  positionInVehicle: string
  defectType: string
  criticality: string
  restrictionCode: string
  affectToUsage: string
  defectCode: string
  vehicleType: string
  vehicleSerialNumber: string
}

export type CreateDefectCommentFormData = {
  comment: string
  defectId: string
}

export type DefectGroup = {
  id: string
  description: string
  useForCreateDefect: boolean
  createDefectRequirePicture: boolean
  type: string
  sortIndex: number
}

export type OptionStates = {
  [Key in keyof OptionTypes]: dataWithStatus<OptionTypes[Key]>
}

export type CommonDataState = OptionStates

export interface LimitationOfUsage {
  id: string
  description: string
}

export interface PositionInVehicle {
  position: string
  description: string
}

export type Equipment = {
  installationGroup: string
  description: string
  itemId: string
  fleet?: string
}

export interface AffectToUsage {
  id: string
  description: string
}

export type DefectTree = {
  productGroups: string
  defectType: string
  criticality: '1' | '2' | '3'
  defectCode: string
  mpgDescription: string
  spgDescription: string
  vrpg1Description: string
  vrpg2Description: string
}

export type OptionTypes = {
  equipments: Equipment[]
  defectGroups: DefectGroup[]
  defectTrees: DefectTree[]
  positions: PositionInVehicle[]
  limitationsOfUsage: LimitationOfUsage[]
  affectToUsage: AffectToUsage[]
}

export interface dataWithStatus<T> {
  data: T
  status: ActionStatus
  error?: string
}

export type CreateDefectResponse = {
  id: string
  criticality: string
}

export interface OptionFetch<T extends keyof OptionTypes> {
  id: T
  data: OptionTypes[T]
}

export type OptionsWithID<T extends keyof OptionTypes> = Promise<OptionFetch<T>>

interface BaseFieldConfigProps<T> {
  disabled?: boolean
  value?: T
  onChange?: (value: T | null | undefined) => void
  required?: boolean
  status?: ActionStatus
}

export type DropdownItem = {
  value: string | null
  label: string
  disabled?: boolean
}

type SelectionTypes = Pick<
  CreateDefectFormData,
  | 'defectGroup'
  | 'installationGroup'
  | 'positionInVehicle'
  | 'defectCodePrefix'
  | 'defectType'
  | 'criticality'
  | 'restrictionCode'
  | 'affectToUsage'
>

type SelectionConfig = {
  [key in keyof SelectionTypes]: BaseFieldConfigProps<SelectionTypes[key]> & {
    options: DropdownItem[]
    checkboxOnChange?: (value?: boolean) => void
    checkboxRequired?: boolean
    multiline?: boolean
  }
}

export interface AssetIdentifier {
  id: string
  filename: string
}

export type AssetIdentifiers = {
  [id: AssetIdentifier['id']]: AssetIdentifier['filename']
}

export type AssetBytes = {
  [id: AssetIdentifier['id']]: { base64: string; mimeType: string }
}

type DigitalAssetConfig = {
  digitalAssetUuids: BaseFieldConfigProps<CreateDefectFormData['digitalAssetUuids']> & {
    files: AssetIdentifiers
  }
}

type OtherFormTypes = Pick<CreateDefectFormData, 'comment' | 'issuedTime'>

type OtherFormConfig = {
  [key in keyof OtherFormTypes]: BaseFieldConfigProps<OtherFormTypes[key]>
}

export type FieldConfig = OtherFormConfig & SelectionConfig & DigitalAssetConfig

export interface FormControls<T extends FieldValues> {
  name: FieldPath<T>
  label?: string
  required?: boolean
  isDisabled?: boolean
  status?: ActionStatus
}

export interface AutoCompleteOptions {
  options: DropdownItem[]
  customOnChange?: (value: string | null) => void
  defaultValue?: string
  multiline?: boolean
}

export enum inputTypes {
  input = 'input',
  upload = 'upload',
  datetime = 'datetime',
  auto = 'auto',
  autoWithCheckbox = 'checkbox',
}

interface BaseFieldData {
  title?: string
  required?: boolean
  key: keyof CreateDefectFormData
  status?: ActionStatus
  disabled?: boolean
  order?: number
}

export interface AutoComplete extends BaseFieldData {
  type: inputTypes.auto
  options: DropdownItem[]
  onChange?: (value: string | null) => void
  value?: string
  multiline?: boolean
}

export interface Input extends BaseFieldData {
  type: inputTypes.input
}

export interface Upload extends BaseFieldData {
  type: inputTypes.upload
  instructions: string
  fileWord: string
  files: AssetIdentifiers
  onChange?: (value?: string[]) => void
}

export type FieldData = Input | Upload | AutoComplete

export interface CardData {
  title?: string
  placeholder?: string
  data: FieldData[]
}

export interface SvgProps {
  fill?: string
  size?: number
  stroke?: string
}

export type Status = 'open' | 'closed'
